/**
 * Where each ingestion provider's import surface lives.
 *
 * The route is the ONE thing about an importer the frontend has to own — the
 * backend registry knows a provider's key, its label and what it accepts, but
 * nothing about React Router. Everything else about a destination (its name)
 * rides along from the provider's own `label_i18n_key`, so this table stays a
 * list of paths rather than a second, drift-prone copy of the registry.
 *
 * Two consumers:
 * - the mismatch banner's destination link ("import it under {importer}"),
 *   driven by `DocumentClassification.destinations` on the backend;
 * - {@link INGESTION_RULES_PAGES}, whose "back to import" breadcrumb is this
 *   same path.
 *
 * Some importers launch from a parent entity (a vendor's catalogue) and so have
 * no standalone import route — those point at the parent list, which is where
 * the user came from and where the import action lives.
 */

/** Provider `target_entity_type` -> the route its import surface is reached at. */
export const INGESTION_IMPORT_SURFACES: Readonly<Record<string, string>> = {
  'accounting.bill': '/accounting/import-bills',
  'procurement.purchase_order': '/procurement/purchase-orders/import-pro-formas',
  'procurement.goods_receipt': '/procurement/goods-receipts/import',
  // Launched from an RFQ ('/procurement/rfqs/:rfqId/import-quote'), so the RFQ
  // list is where the user picks which one they are importing a quote against.
  'procurement.supplier_pi': '/procurement/rfqs',
  'master_data.product': '/master-data/products/data-sheet-import',
  // Same shape: launched from a vendor's detail page, so there is no import
  // route without a vendor id.
  'master_data.supplier_price_list': '/procurement/vendors',
  'sales.inquiry': '/sales/inquiries/import',
  'contracts.contract': '/contracts/import',
};

export function ingestionImportSurfaceFor(targetEntityType: string): string | undefined {
  return INGESTION_IMPORT_SURFACES[targetEntityType];
}

/**
 * The importers whose review route is nested under a PARENT record, keyed by the
 * `origin_context` entry that names it.
 *
 * For these, `INGESTION_IMPORT_SURFACES` holds the parent LIST (the page the user
 * came from), so the generic `${surface}/${batchId}` composition below does not
 * merely fail to reach the review — it builds a path that RESOLVES to something
 * else. `/procurement/vendors/{batchId}` matches `/procurement/vendors/:vendorId`
 * and renders a vendor detail page failing to load a vendor whose id is a batch
 * id: no 404, no error, just a broken-looking record (the same shape
 * `.claude/rules/rules-frontend.md` records for `${prefix}`-built links). So a
 * parent-scoped provider resolves ONLY from its origin, and answers `undefined`
 * without one — `IngestionSourceImportLink` renders nothing rather than a wrong
 * destination.
 */
const PARENT_SCOPED_IMPORT_RECORDS: Readonly<
  Record<string, { originKey: string; href: (parentId: string, batchId: string) => string }>
> = {
  'procurement.supplier_pi': {
    originKey: 'rfq_id',
    href: (rfqId, batchId) => `/procurement/rfqs/${rfqId}/import-quote/${batchId}`,
  },
  'master_data.supplier_price_list': {
    originKey: 'vendor_id',
    href: (vendorId, batchId) =>
      `/procurement/vendors/${vendorId}/import-catalogue/${batchId}`,
  },
};

/** Resolve the review URL for one concrete import batch. */
export function ingestionImportRecordHrefFor(
  targetEntityType: string,
  batchId: string,
  originContext: Record<string, unknown>,
): string | undefined {
  const parentScoped = PARENT_SCOPED_IMPORT_RECORDS[targetEntityType];
  if (parentScoped) {
    const parentId = originContext[parentScoped.originKey];
    return typeof parentId === 'string' && parentId
      ? parentScoped.href(parentId, batchId)
      : undefined;
  }

  const surface = ingestionImportSurfaceFor(targetEntityType);
  return surface ? `${surface}/${batchId}` : undefined;
}
